
import { useEffect, useState } from 'react';
import Link from '@/components/compat/Link';
import { useRouter } from '@/lib/navigation';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { HeroBackdropLayers } from '@/components/help-center/latest-power-shared';
import { MaskedSvgIcon } from '@/components/help-center/MaskedSvgIcon';
import AdminStarsLayer from '@/components/admin/AdminStarsLayer';
import AdminLoginFloatingSmilies from '@/components/admin/AdminLoginFloatingSmilies';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import { painelButtonPrimaryClass } from '@/components/painel/painel-styles';
import {
  ADMIN_DASHBOARD_PATH,
  attemptAdminLogin,
  isAdminAuthenticated,
} from '@/lib/admin-auth';
import { isDevAuthEnabled } from '@/lib/dev-auth';
import { cn } from '@/lib/utils';

const loginInputClass = cn(
  painelInputClass,
  'outline-none transition-[border-color,background-color]',
  'focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0',
  'focus:border-sky-400/45'
);

export default function AdminLoginPage() {
  const router = useRouter();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    if (isAdminAuthenticated()) {
      router.replace(ADMIN_DASHBOARD_PATH);
      return;
    }
    setCheckingSession(false);
  }, [router]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (loading) return;

    setError(null);
    setLoading(true);

    const result = await attemptAdminLogin(username, password, remember);

    if (result.ok) {
      router.replace(ADMIN_DASHBOARD_PATH);
      return;
    }

    setLoading(false);

    setError(result.message);
    toast.error(result.message, {
      className: 'font-inter',
      style: {
        background: 'rgba(11, 20, 40, 0.96)',
        border: '1px solid rgba(248, 113, 113, 0.35)',
        color: '#f8fafc',
      },
    });
  }

  if (checkingSession) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#030023]">
        <Loader2 className="h-8 w-8 animate-spin text-sky-400/80" aria-label="A carregar" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#030023] font-inter text-blue-50">
      <HeroBackdropLayers className="absolute inset-0" />
      <AdminStarsLayer />
      <AdminLoginFloatingSmilies />

      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#030023]/20 via-[#000b6a]/15 to-[#030023]/30"
        aria-hidden
      />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className={cn(
            'w-full max-w-[440px] overflow-visible rounded-3xl border border-white/10',
            'bg-blue-950/40 p-8 shadow-[0_24px_80px_-24px_rgba(0,0,0,0.75),0_0_60px_-20px_rgba(56,189,248,0.18)]',
            'backdrop-blur-xl sm:p-9'
          )}
        >
          <div className="mb-10 flex justify-center overflow-visible">
            <Link href="/" className="group inline-flex flex-col items-center gap-1 overflow-visible pt-0.5">
              <span className="relative inline-block w-fit">
                <span className="whitespace-nowrap font-poppins text-4xl font-bold leading-none tracking-tight text-white transition-colors group-hover:text-blue-100 sm:text-5xl">
                  xat Ajuda
                </span>
                <span
                  className={cn(
                    'absolute right-0 top-0 z-10',
                    'translate-x-[calc(58%+3px)] -translate-y-[calc(42%+4px)]',
                    'inline-flex items-center justify-center rounded-full',
                    'px-2 py-[2px] font-poppins text-[8px] font-bold uppercase leading-none tracking-[0.1em] text-white',
                    'bg-gradient-to-br from-sky-400 to-[#000b6a] shadow-md shadow-sky-950/50 ring-1 ring-white/25',
                    'sm:px-2 sm:py-0.5 sm:text-[9px] sm:tracking-[0.12em]'
                  )}
                  title="ÁREA 42"
                >
                  ÁREA 42
                </span>
              </span>
            </Link>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div className="space-y-2">
              <Label htmlFor="admin-username" className="font-inter text-sm text-slate-300">
                Usuário
              </Label>
              <div className="relative">
                <MaskedSvgIcon
                  src="/icons/user.svg"
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sky-300/50"
                />
                <input
                  id="admin-username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  className={cn(loginInputClass, 'pl-10')}
                  placeholder="Digite seu usuário"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-password" className="font-inter text-sm text-slate-300">
                Senha
              </Label>
              <div className="relative">
                <MaskedSvgIcon
                  src="/icons/lock.svg"
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sky-300/50"
                />
                <input
                  id="admin-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  className={cn(loginInputClass, 'pr-10 pl-10')}
                  placeholder="Digite sua senha"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-0.5 text-slate-400 transition-colors hover:text-sky-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40"
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <Checkbox
                id="admin-remember"
                checked={remember}
                onCheckedChange={(checked) => setRemember(checked === true)}
                disabled={loading}
                className="border-white/20 bg-white/[0.06] data-[state=checked]:border-sky-400/60 data-[state=checked]:bg-sky-500"
              />
              <Label
                htmlFor="admin-remember"
                className="cursor-pointer font-inter text-sm font-normal text-slate-400"
              >
                Lembrar de mim
              </Label>
            </div>

            <motion.button
              type="submit"
              disabled={loading}
              whileHover={loading ? undefined : { scale: 1.01 }}
              whileTap={loading ? undefined : { scale: 0.99 }}
              transition={{ duration: 0.15 }}
              className={cn(
                painelButtonPrimaryClass,
                'flex h-11 w-full items-center justify-center gap-2 text-sm',
                'disabled:cursor-not-allowed disabled:opacity-70'
              )}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Entrando…
                </>
              ) : (
                'Entrar'
              )}
            </motion.button>

            {error ? (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center font-inter text-sm text-rose-300/90"
                role="alert"
              >
                {error}
              </motion.p>
            ) : null}

            {isDevAuthEnabled() ? (
              <p className="text-center font-inter text-xs leading-relaxed text-slate-500">
                Desenvolvimento: use <span className="text-slate-400">admin</span> /{' '}
                <span className="text-slate-400">admin</span> (ou outro utilizador mock com a mesma
                senha).
              </p>
            ) : null}
          </form>
        </motion.div>
      </div>
    </div>
  );
}
